import { api } from './api';

class ProcessFlowService {
  constructor() {
    this.baseURL = '/process-flows';
  }

  // Get all process flows
  async getProcessFlows(params = {}) {
    try {
      const response = await api.get(this.baseURL, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get single process flow
  async getProcessFlow(id) {
    try {
      const response = await api.get(`${this.baseURL}/${id}`);
      // Handle various response structures
      return response.data?.data?.processFlow || response.data?.processFlow || response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Create process flow
  async createProcessFlow(data) {
    try {
      const response = await api.post(this.baseURL, data);
      // Handle various response structures
      return response.data?.data?.processFlow || response.data?.processFlow || response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Update process flow
  async updateProcessFlow(id, data) {
    try {
      const response = await api.patch(`${this.baseURL}/${id}`, data);
      // Handle various response structures
      return response.data?.data?.processFlow || response.data?.processFlow || response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Delete process flow
  async deleteProcessFlow(id) {
    try {
      await api.delete(`${this.baseURL}/${id}`);
      return true;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Auto-save process flow
  async autoSaveProcessFlow(id, data) {
    try {
      const response = await api.post(`${this.baseURL}/${id}/auto-save`, data);
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Share process flow
  async shareProcessFlow(id, shareData) {
    try {
      const response = await api.post(`${this.baseURL}/${id}/share`, shareData);
      // Handle various response structures
      return response.data?.data?.processFlow || response.data?.processFlow || response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Unshare process flow
  async unshareProcessFlow(id, userId) {
    try {
      const response = await api.delete(`${this.baseURL}/${id}/share/${userId}`);
      // Handle various response structures
      return response.data?.data?.processFlow || response.data?.processFlow || response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Add comment
  async addComment(id, commentData) {
    try {
      const response = await api.post(`${this.baseURL}/${id}/comments`, commentData);
      // Handle various response structures
      return response.data?.data?.processFlow || response.data?.processFlow || response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Resolve comment
  async resolveComment(id, commentId) {
    try {
      const response = await api.patch(
        `${this.baseURL}/${id}/comments/${commentId}/resolve`
      );
      return response.data?.data?.processFlow || response.data?.processFlow || response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Duplicate process flow
  async duplicateProcessFlow(id, data = {}) {
    try {
      const response = await api.post(`${this.baseURL}/${id}/duplicate`, data);
      return response.data?.data?.processFlow || response.data?.processFlow || response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get templates
  async getTemplates(category) {
    try {
      const params = category ? { category } : {};
      const response = await api.get(`${this.baseURL}/templates`, { params });
      return response.data?.data?.templates || response.data?.templates || response.data || [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Create from template
  async createFromTemplate(templateId, data) {
    try {
      const response = await api.post(
        `${this.baseURL}/templates/${templateId}`,
        data
      );
      return response.data?.data?.processFlow || response.data?.processFlow || response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Convert to template
  async convertToTemplate(id, category) {
    try {
      const response = await api.post(`${this.baseURL}/${id}/convert-to-template`, {
        category
      });
      return response.data?.data?.processFlow || response.data?.processFlow || response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Export process flow
  async exportProcessFlow(id, format = 'json') {
    try {
      const response = await api.get(`${this.baseURL}/${id}/export`, {
        params: { format },
        responseType: format === 'json' ? 'blob' : 'json'
      });

      if (format === 'json') {
        // Create download link for file
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `process_flow_${id}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return true;
      }

      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Import process flow
  async importProcessFlow(data) {
    try {
      const response = await api.post(`${this.baseURL}/import`, data);
      return response.data?.data?.processFlow || response.data?.processFlow || response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get versions
  async getVersions(id) {
    try {
      const response = await api.get(`${this.baseURL}/${id}/versions`);
      return response.data?.data || response.data || [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Restore version
  async restoreVersion(id, versionNumber) {
    try {
      const response = await api.post(
        `${this.baseURL}/${id}/versions/${versionNumber}/restore`
      );
      return response.data?.data?.processFlow || response.data?.processFlow || response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get shared with me
  async getSharedWithMe() {
    try {
      const response = await api.get(`${this.baseURL}/shared`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get my process flows
  async getMyProcessFlows() {
    try {
      const response = await api.get(`${this.baseURL}/mine`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Handle errors
  handleError(error) {
    if (error.response) {
      const message = error.response.data?.message || 'An error occurred';
      const errorObj = new Error(message);
      errorObj.status = error.response.status;
      errorObj.data = error.response.data;
      return errorObj;
    }
    return error;
  }

  // Upload file for import
  async uploadFlowFile(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`${this.baseURL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Search process flows
  async searchProcessFlows(query) {
    try {
      const response = await api.get(`${this.baseURL}/search`, {
        params: { q: query }
      });
      return response.data.data.processFlows;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get process flow by code
  async getProcessFlowByCode(code) {
    try {
      const response = await api.get(`${this.baseURL}/code/${code}`);
      return response.data.data.processFlow;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Batch operations
  async batchDelete(ids) {
    try {
      const response = await api.post(`${this.baseURL}/batch/delete`, { ids });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async batchShare(ids, shareData) {
    try {
      const response = await api.post(`${this.baseURL}/batch/share`, {
        ids,
        ...shareData
      });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async batchUpdateStatus(ids, status) {
    try {
      const response = await api.post(`${this.baseURL}/batch/status`, {
        ids,
        status
      });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get activity log for a process flow
  async getActivityLog(id) {
    try {
      const response = await api.get(`${this.baseURL}/${id}/activity`);
      return response.data.data.activities;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Clone to another project
  async cloneToProject(id, projectId) {
    try {
      const response = await api.post(`${this.baseURL}/${id}/clone`, {
        projectId
      });
      return response.data.data.processFlow;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Validate flow structure
  async validateFlow(nodes, edges) {
    try {
      const response = await api.post(`${this.baseURL}/validate`, {
        nodes,
        edges
      });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

const processFlowService = new ProcessFlowService();
export default processFlowService;